import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg'
import Button from '@/components/Button'
import DataTable from '@/components/DataTable/index'
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import { ReactComponent as IconConfigure } from '@/assets/svgs/icon-configure.svg';
import Portal from '@/components/Portal'
import Toast2 from '@/components/Toast2'
import useConfig from '@/hooks/use-config'
import useDataPage from '@/hooks/use-data-page/index'
import useFilter from '@/hooks/use-filter'
import useMessages from '@/hooks/use-messages'
import useModal from '@/hooks/use-modal/index'
import usePage from '@/hooks/use-page'
import "@/translations/i18n"
import rem from '@/utils/rem.js'
import { produce } from 'immer'
import { orderBy } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from "react-i18next"
import { useSelector } from 'react-redux'
import { DATA_TABLE_PARENT } from '@/constants/index.js'
import MultiSelectCheckbox from '@/components/MultiSelectCheckbox/index'
import SearchInput from '../shared-data/components/Search/index'
import MultiSelectFilter from '../shared-data/components/MultiSelectFilter/index'
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx'
import { useBuildex } from "@/hooks/use-buildex";

const Page = () => {

	/*=================================================
		General
	/================================================*/

	const { t } = useTranslation();
	const [pageTitle, setPageTitle] = useState(t("receivedData"))
	const [refIdSearch, setRefIdSearch] = useState('')

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Received Data',
				link: `/received-data`
			}
		]
	})

	const pageLimit = 10

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()
	function handleError() {
		setError()
	}

	/*=================================================
		API
	/================================================*/

	const { config, isConsumer, fetch: isAbleToPassiveConsume } = useConfig()
	const { items: messages, meta: messagesMeta, postFetch, error: erroMessages } = useMessages()
	const { filter, fetch: fetchFilter } = useFilter()
	const [simulate, setSimulate] = useState(false)
	const isBuildex = useBuildex()

	const { error: dataExpiryError } = useSelector(state => state.dataExpiry)

	function updateSimulateState(_config) {
		let _simulate = false
		if (_config && (_config.isSandbox || !_config.otherUrl)) {
			_simulate = true
		}
		setSimulate(_simulate)
	}

	useEffect(() => {
		async function getData() {
			fetchFilter({ messageType: 'PULL,RECEIVE' })
		}
		getData()
	}, [])

	useEffect(() => {
		updateSimulateState(config)
		fetchMessagesHandler()
	}, [config])

	useEffect(() => {
		if (dataExpiryError) {
			setError(dataExpiryError)
		} else {
			setError(erroMessages)
		}
	}, [dataExpiryError, erroMessages])

	function fetchMessagesHandler() {
		if (currentPage) {

			postFetch({
				dataElementId: convertArrayToString(dataElementsOptions) ?? null,
				page: currentPage,
				orgId: convertArrayToString(participantsOptions) ?? null,
				limit: pageLimit,
				messageStatus: convertArrayToString(statusOptions) ?? null,
				messageType: convertArrayToString(typeOptions) !== null ? convertArrayToString(typeOptions) : 'PULL,RECEIVE',
				dateFrom: startDate !== null ? moment(startDate).format('yyyy-MM-DD HH:mm') : null,
				dateTo: endDate !== null ? moment(endDate).format('yyyy-MM-DD HH:mm') : null,
				sortBy: sortConfig.key,
				order: sortConfig.direction === true ? "ASC" : "DESC",
				receivedTxnType: convertArrayToString(receivingTypeOptions)?.replace("COPY_FAIL", "COPY") ?? null,
				providerTxnStatus: providerTransactionStatusOptions.length > 0 ? providerTransactionStatusOptions.join(",") : null,
				referenceId: refIdSearch
			})
		}
	}

	/*=================================================
		Modal
	/================================================*/

	const { visible: visibleDownloadData, hide: hideDownloadData, show: showDownloadData } = useModal()

	/*=================================================
		Options
	/================================================*/

	const { fileSize, checkFileSize } = useDataPage()
	const { dataElementsOptions, setDataElementsOptions, typeOptions, setTypeOptions } = useDataPage()
	const { participantsOptions, setParticipantsOptions, statusOptions, setStatusOptions } = useDataPage({ parentComponentName: "received-data" })
	const [providerTransactionStatusOptions, setProviderTransactionStatusOptions] = useState([])

	const [receivingTypeOptions, setReceivingTypeOptions] = useState(
		[
			{ id: "COPY", label: "Copy - Consumer Success", $selected: false },
			{ id: "COPY_FAIL", label: "Copy - Consumer Failed", $selected: false },
			{ id: "DIRECT", label: "Direct Consume", $selected: false }
		]
	);

	useEffect(() => {
		if (receivingTypeOptions && receivingTypeOptions.length > 0) {
			const updatedOptions = receivingTypeOptions.filter(item => (item.$selected === true));
			let auxOptions = [];
			let hasCopyOrCopyFail = false;

			updatedOptions.forEach(option => {
				if (option.id === "COPY") {
					auxOptions.push("SUCCESS");
					hasCopyOrCopyFail = true;
				}
				if (option.id === "COPY_FAIL") {
					auxOptions.push("FAIL");
					hasCopyOrCopyFail = true;
				}
			});
			const allChosen = receivingTypeOptions.every(option => option.$selected === true);

			if (!hasCopyOrCopyFail || allChosen) {
				auxOptions = [];
			}

			setProviderTransactionStatusOptions(auxOptions);
		}
	}, [receivingTypeOptions]);

	const formatOptions = (
		source,
		idKey,
		labelKey,
		setter
	) => {
		if (!source || source.length === 0) {
			return;
		}

		const updatedOptions = source.map(item => ({
			id: item[idKey],
			label: item[labelKey],
			$selected: false,
		}));
		const sortedOptions = orderBy(
			updatedOptions,
			[option => option.label.toLowerCase()],
			['asc']
		);

		setter(sortedOptions);
	};

	useEffect(() => {
		if (filter?.dataElements || filter?.dataElements.length != 0) {
			formatOptions(filter?.dataElements, 'dataElementId', 'elementName', setDataElementsOptions);
		}

		if (filter?.orgs || filter?.orgs.length != 0) {
			formatOptions(filter?.orgs, 'orgId', 'name', setParticipantsOptions);
		}
	}, [filter])


	const [startDate, setStartDate] = useState(null)
	const [endDate, setEndDate] = useState(null)

	useEffect(() => {
		return setEndDate(null);
	}, [startDate]);

	/*=================================================
		Sort
	/================================================*/

	const { sortConfig } = useDataPage()

	/*=================================================
		Paginate
	/================================================*/

	const { currentPage, setCurrentPage, goToNext, goToPrev, handleCurrentInput, updatedData, changeAllListCheckboxes, changeListCheckboxes } = useDataPage()

	useEffect(() => {
		fetchMessagesHandler()
	}, [currentPage, dataElementsOptions, participantsOptions, statusOptions, typeOptions, startDate, endDate, sortConfig, providerTransactionStatusOptions,refIdSearch])

	useEffect(() => {
		if (currentPage > messagesMeta?.totalPages) {
			setCurrentPage(messagesMeta?.totalPages || 1)
		}
	}, [messages])

	/*=================================================
		Routes
	/================================================*/

	const { goToRequest, goToSimulateReceive } = useDataPage()

	/*=================================================
		Data Table
	/================================================*/

	const ReceivedDataTable = ({ itemData, type }) => {
		const args = {
			itemData: itemData,
			type: type,
			parentComponentName: DATA_TABLE_PARENT.RECEIVED_DATA,
			currentPage: currentPage,
			goToNextPage: goToNext,
			goToPrevPage: goToPrev,
			meta: messagesMeta,
			handleCurrentInput: handleCurrentInput,
			changeAllListCheckboxes: changeAllListCheckboxes,
			changeListCheckboxes: changeListCheckboxes,
			showModal: showDownloadData,
			checkFileSize: checkFileSize
		}

		return DataTable(args);
	}

	/*=================================================
		Function
	/================================================*/

	const { convertArrayToString, downloadFile, getOverviewClass, setFileType, setIncludeAttachments, fileType, includeAttachments, isAnySelected } = useDataPage()

	function resetReceivedDataFilter() {
		setDataElementsOptions(setFalse(dataElementsOptions))
		setParticipantsOptions(setFalse(participantsOptions))
		setStatusOptions(setFalse(statusOptions))
		setTypeOptions(setFalse(typeOptions))
		setStartDate(null)
		setEndDate(null)
		setRefIdSearch("")
		setReceivingTypeOptions(setFalse(receivingTypeOptions))
		function setFalse(array) {
			const updatedItems = produce(array, draft => {
				draft.forEach(
					item => item.$selected = false
				)
			})
			return updatedItems
		}
	}

	const isFilterActive = useMemo(() => {
		return !!(
			isAnySelected(dataElementsOptions) ||
			isAnySelected(participantsOptions) ||
			isAnySelected(typeOptions) ||
			isAnySelected(receivingTypeOptions) ||
			isAnySelected(statusOptions) ||
			startDate ||
			endDate
		  )
	  }, [dataElementsOptions, participantsOptions, statusOptions, startDate, endDate, typeOptions]);

	const receivedFilterConfig = [
		{
		  id: "dataElement",
		  title: "Data Element",
		  component: MultiSelectCheckbox,
		  props: {
			items: dataElementsOptions,
			onChangeFunc: setDataElementsOptions,
			disable: !isConsumer || (messages.length === 0 && (isBuildex ? !isFilterActive : true)),
		  },
		},
		{
		  id: "sender",
		  title: "Sender",
		  component: MultiSelectCheckbox,
		  props: {
			items: participantsOptions,
			onChangeFunc: setParticipantsOptions,
			disable: !isConsumer || (messages.length === 0 && (isBuildex ? !isFilterActive : true)),
		  },
		},
		{
		  id: "receivingType",
		  title: "Receiving Type",
		  className: "overview_filter__col--receiving-type",
		  component: MultiSelectCheckbox,
		  props: {
			items: receivingTypeOptions,
			onChangeFunc: setReceivingTypeOptions,
			disable: !isConsumer || (messages.length === 0 && (isBuildex ? !isFilterActive : true)),
		  },
		},
		{
		  id: "type",
		  title: "Type",
		  component: MultiSelectCheckbox,
		  props: {
			items: typeOptions,
			onChangeFunc: setTypeOptions,
			disable: !isConsumer || (messages.length === 0 && (isBuildex ? !isFilterActive : true)),
		  },
		}
	  ];
	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
				<div className="title-header__right">
					<div className="overview__filter overview__button_wrap">
						{!isBuildex && simulate && (
							<Button dotted full round text={t("simulateReceive")} id="simulate-receive-button-id" submit={goToSimulateReceive} disabled={!isConsumer} />
						)}

						<Button round full text={t("requestData")} id="receive-data-button-id" submit={goToRequest} disabled={!isAbleToPassiveConsume} />
					</div>
				</div>
			</div>

			<div className={getOverviewClass}>
			<MultiSelectFilter
			    filterConfig={receivedFilterConfig}
				statusOptions={statusOptions}
				setStatusOptions={setStatusOptions}
				startDate={startDate}
				setStartDate={setStartDate}
				endDate={endDate}
				setEndDate={setEndDate}
				resetFilter={resetReceivedDataFilter}
				disableFilters={!isConsumer || (messages.length === 0 && (isBuildex ? !isFilterActive : true))}
				/>

               <SearchInput
                value={refIdSearch}
                placeholder="Search by Reference ID"
                onChange={(e) => setRefIdSearch(e?.target.value)}
                onCancel={() => setRefIdSearch('')}
               />

				<div className="overview__content">
					{ReceivedDataTable({ itemData: updatedData.length > 0 ? updatedData : messages })}
				</div>
			</div>

			<Modal visible={visibleDownloadData } className="received__data" variant='medium' close={hideDownloadData}>
				<CardConfigure >
					<CardConfigure.Header className="received__data">
						<CardConfigure.Header.Title className="received__data">
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<div className={'card-configure__icon'}>
											<IconConfigure />
										</div>
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											Download Transaction
										</div>
									</div>
								</div>
							</div>

						</CardConfigure.Header.Title >
					</CardConfigure.Header>
					<CardConfigure.Body className="received__data">
						<CardConfigure.Body.Col className="received__data">
							<div className="download-transaction__rows received__data">
								<div className="download-transaction__row received__data">
									You are about to export {updatedData.filter(message => message.$selected === true).length} transaction ({fileSize}MB)
								</div>
								<div className="download-transaction__row received__data">
									<div className="download-transaction__row__left">
										Export data as
									</div>
									<div className="download-transaction__row__right received__data">
										<div className="download-transaction__row__right__radio received__data">
											<input type="radio" value="json" name="fileType" id="json"
												checked={fileType == "json"}
												onChange={(e) => setFileType(e.target.value)}
											/> <label htmlFor="json">JSON</label>
										</div>
										<div className="download-transaction__row__right__radio received__data">
											<input type="radio" value="csv" name="fileType" id="csv"
												checked={fileType == "csv"}
												onChange={(e) => setFileType(e.target.value)}
											/> <label htmlFor="csv">CSV</label>
										</div>
									</div>
								</div>
								<div className="download-transaction__row received__data">
									<div className="download-transaction__row__left received__data">
										Include attachments
									</div>
									<div className="download-transaction__row__right received__data">
										<div className="download-transaction__row__right__radio received__data">
											<input type="radio" value={true} name="includeAttachment" id="yes"
												checked={includeAttachments == JSON.parse("true")}
												onChange={() => setIncludeAttachments(true)}
												disabled={fileSize > 250}
											/> <label htmlFor="yes">Yes</label>
											{
												(fileSize > 250) && (
													<span className="download-transaction__row__right__radio__message">
														Download size is too large
													</span>
												)
											}
										</div>
										<div className="download-transaction__row__right__radio received__data">
											<input type="radio" value={false} name="includeAttachment" id="no"
												checked={includeAttachments == JSON.parse("false")}
												onChange={() => setIncludeAttachments(false)}
											/> <label htmlFor="no">No</label>
											<span className="download-transaction__row__right__radio__message">
												&nbsp; (download data only)
											</span>
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer className="received__data">
						<CardConfigure.Footer.Right className="received__data">
							<Button text="Download"
								disabled={fileType ? false : true}
								submit={() => {
									downloadFile(updatedData)
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>
			<Toast2 className="received__data" show={error} errorMessage={error} close={() => { handleError() }} />
		</>
	)
}

export default Page
